import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { CNetRow, CNetActions, CNetResult, G } from './_cnet';

const AREA_TO_SQFT = { 'square feet': 1, 'square meters': 10.7639, 'square yards': 9 };
const LEN_TO_FT = { feet: 1, meters: 3.28084, inches: 1 / 12 };
const ROOM = { Bedroom: 1, 'Living Room': 1.1, Kitchen: 1.4, 'Home Office': 1.05, Hall: 1.15, Bathroom: 1.2 };
const INSUL = { Poor: 1.2, Average: 1, Good: 0.85 };
const SUN = { Shaded: 0.9, Average: 1, 'Very Sunny': 1.1 };
const CLIMATE = { Cold: 20, Average: 25, Warm: 30, Hot: 35 };
const T_UNITS = { Celsius: 1.8, Fahrenheit: 1 };

// EnergyStar.gov cooling-capacity lookup table (as provided by user).
// Rows: [area low, area high, BTU/h].
const ENERGYSTAR = [
  [100, 150, 5000],
  [150, 250, 6000],
  [250, 300, 7000],
  [300, 350, 8000],
  [350, 400, 9000],
  [400, 450, 10000],
  [450, 550, 12000],
  [550, 700, 14000],
  [700, 1000, 18000],
  [1000, 1200, 21000],
  [1200, 1400, 23000],
  [1400, 1500, 24000],
  [1500, 2000, 30000],
  [2000, 2500, 34000],
];
function energyStarBtu(sqft) {
  for (const [lo, hi, btu] of ENERGYSTAR) if (sqft >= lo && sqft <= hi) return btu;
  if (sqft < ENERGYSTAR[0][0]) return ENERGYSTAR[0][2];
  const last = ENERGYSTAR[ENERGYSTAR.length - 1];
  // extrapolate beyond 2500 sqft using the last-row density
  return Math.round(last[2] * (sqft / last[1]));
}

export default function BTUCalc() {
  // Section 1 - AC BTU (room)
  const [size, setSize] = useState(18);
  const [sizeU, setSizeU] = useState('square meters');
  const [ceilH, setCeilH] = useState(3);
  const [ceilU, setCeilU] = useState('meters');
  const [people, setPeople] = useState(2);
  const [rtype, setRtype] = useState('Bedroom');
  const [insul, setInsul] = useState('Average');
  const [sun, setSun] = useState('Average');
  const [clim, setClim] = useState('Average');
  const [res1, setRes1] = useState(null);

  const calc1 = () => {
    const sqft = Number(size) * AREA_TO_SQFT[sizeU];
    const ftH = Number(ceilH) * LEN_TO_FT[ceilU];
    // Base BTU from EnergyStar.gov table (area-based), then adjust for ceiling / people / room / insulation / sun.
    const base = energyStarBtu(sqft);
    let btu = base;
    btu *= ftH / 8;                          // adjust for non-standard ceiling
    btu *= INSUL[insul];                     // insulation quality
    btu *= SUN[sun];                         // sun exposure
    btu += Math.max(0, Number(people) - 2) * 600; // extra bodies
    btu *= ROOM[rtype] || 1;                 // room type
    // Warm/cold climates shift base capacity by ~10%
    const climFactor = { Cold: 0.85, Average: 1, Warm: 1.1, Hot: 1.2 }[clim] || 1;
    btu *= climFactor;
    setRes1({ btu, base, sqft });
  };
  const clear1 = () => setRes1(null);

  // Section 2 - General purpose
  const [w, setW] = useState(5); const [wU, setWU] = useState('meters');
  const [l, setL] = useState(5); const [lU, setLU] = useState('meters');
  const [h, setH] = useState(3); const [hU, setHU] = useState('meters');
  const [insul2, setInsul2] = useState('normal');
  const [dT, setDT] = useState(25); const [dTU, setDTU] = useState('Celsius');
  const [res2, setRes2] = useState(null);

  const calc2 = () => {
    const wm = Number(w) * (wU === 'meters' ? 1 : wU === 'feet' ? 0.3048 : 0.9144);
    const lm = Number(l) * (lU === 'meters' ? 1 : lU === 'feet' ? 0.3048 : 0.9144);
    const hm = Number(h) * (hU === 'meters' ? 1 : hU === 'feet' ? 0.3048 : 0.9144);
    const volM3 = wm * lm * hm;
    // rough BTU: 0.1337 × volume(ft³) × ΔT(°F)  → but simpler: kJ needed ~ 1.2·V·ΔT then to BTU
    const volFt3 = volM3 * 35.3147;
    const dtF = Number(dT) * T_UNITS[dTU];
    const factor = insul2 === 'poor' ? 0.15 : insul2 === 'good' ? 0.10 : 0.133;
    const btu = volFt3 * dtF * factor;
    setRes2({ btu });
  };
  const clear2 = () => setRes2(null);

  return (
    <CalcShell look="cnet" showActions={false} title="BTU Calculator" subtitle="Estimate BTU cooling / heating needs for a room or general-purpose HVAC sizing.">
      <h3 className="cnet-section-title">AC BTU Calculator</h3>
      <p className="text-sm text-slate-700 mb-3">Use this calculator to estimate the cooling needs of a typical room or house, such as finding out the power of a window air conditioner needed for an apartment room or the central air conditioner for an entire house.</p>
      <div className="cnet-panel">
        <CNetRow label="Size">
          <input data-testid="btu-size" className="cnet-input" type="number" value={size} onChange={e => setSize(e.target.value)} />
          <select className="cnet-select" value={sizeU} onChange={e => setSizeU(e.target.value)}>{Object.keys(AREA_TO_SQFT).map(k => <option key={k}>{k}</option>)}</select>
        </CNetRow>
        <CNetRow label="Room Ceiling Height">
          <input data-testid="btu-ceil" className="cnet-input" type="number" value={ceilH} onChange={e => setCeilH(e.target.value)} />
          <select className="cnet-select" value={ceilU} onChange={e => setCeilU(e.target.value)}>{Object.keys(LEN_TO_FT).map(k => <option key={k}>{k}</option>)}</select>
        </CNetRow>
        <div className="cnet-row-2col">
          <div className="cnet-label">Number of People<br/>Inside Regularly</div>
          <input data-testid="btu-people" className="cnet-input" type="number" value={people} onChange={e => setPeople(e.target.value)} />
        </div>
        <div className="cnet-row-2col"><div className="cnet-label">Type</div>
          <select data-testid="btu-type" className="cnet-select" value={rtype} onChange={e => setRtype(e.target.value)}>{Object.keys(ROOM).map(k => <option key={k}>{k}</option>)}</select>
        </div>
        <div className="cnet-row-2col"><div className="cnet-label">Insulation Condition</div>
          <select data-testid="btu-insul" className="cnet-select" value={insul} onChange={e => setInsul(e.target.value)}>{Object.keys(INSUL).map(k => <option key={k}>{k}</option>)}</select>
        </div>
        <div className="cnet-row-2col"><div className="cnet-label">Sun Exposure</div>
          <select data-testid="btu-sun" className="cnet-select" value={sun} onChange={e => setSun(e.target.value)}>{Object.keys(SUN).map(k => <option key={k}>{k}</option>)}</select>
        </div>
        <div className="cnet-row-2col"><div className="cnet-label">Climate</div>
          <select data-testid="btu-clim" className="cnet-select" value={clim} onChange={e => setClim(e.target.value)}>{Object.keys(CLIMATE).map(k => <option key={k}>{k}</option>)}</select>
        </div>
        <CNetActions onCalc={calc1} onClear={clear1} testId="btu" />
      </div>

      {res1 && (
        <CNetResult>
          <div className="text-xl font-semibold text-slate-900" data-testid="btu-result">
            Required Cooling Capacity: {G(res1.btu, 0)} BTU/h
          </div>
          <div className="text-sm text-slate-700 mt-1">≈ {G(res1.btu / 12000, 2)} tons · {G(res1.btu * 0.29307107, 0)} W · EnergyStar base for {G(res1.sqft, 0)} sqft: {G(res1.base, 0)} BTU/h</div>
        </CNetResult>
      )}

      <div className="mt-4">
        <h4 className="text-sm font-semibold text-slate-900 mb-2">EnergyStar.gov reference table — cooling capacity vs. floor area</h4>
        <table className="cnet-info-table" data-testid="btu-energystar-table">
          <thead><tr><td><b>Area (sq ft)</b></td><td><b>Capacity (BTU/h)</b></td></tr></thead>
          <tbody>
            {ENERGYSTAR.map(([lo, hi, btu]) => (
              <tr key={lo}><td>{lo.toLocaleString()} to {hi.toLocaleString()}</td><td>{btu.toLocaleString()}</td></tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="cnet-section-title mt-6">General Purpose AC or Heating BTU Calculator</h3>
      <p className="text-sm text-slate-700 mb-3">This is a general purpose calculator that helps estimate the BTUs required to heat or cool an area. The desired temperature change is the necessary increase/decrease from outdoor temperature to reach the desired indoor temperature. As an example, an unheated Boston home during winter could reach temperatures as low as -5°F. To reach a temperature of 75°F, it requires a desired temperature increase of 80°F. This calculator can only gauge rough estimates.</p>
      <div className="cnet-panel">
        <CNetRow label="Room/House Width">
          <input className="cnet-input" type="number" value={w} onChange={e => setW(e.target.value)} />
          <select className="cnet-select" value={wU} onChange={e => setWU(e.target.value)}><option>meters</option><option>feet</option><option>yards</option></select>
        </CNetRow>
        <CNetRow label="Room/House Length">
          <input className="cnet-input" type="number" value={l} onChange={e => setL(e.target.value)} />
          <select className="cnet-select" value={lU} onChange={e => setLU(e.target.value)}><option>meters</option><option>feet</option><option>yards</option></select>
        </CNetRow>
        <CNetRow label="Ceiling Height">
          <input className="cnet-input" type="number" value={h} onChange={e => setH(e.target.value)} />
          <select className="cnet-select" value={hU} onChange={e => setHU(e.target.value)}><option>meters</option><option>feet</option><option>inches</option></select>
        </CNetRow>
        <div className="cnet-row-2col"><div className="cnet-label">Insulation Condition</div>
          <select className="cnet-select" value={insul2} onChange={e => setInsul2(e.target.value)}><option>poor</option><option>normal</option><option>good</option></select>
        </div>
        <CNetRow label="Desired Temperature Increase or Decrease">
          <input className="cnet-input" type="number" value={dT} onChange={e => setDT(e.target.value)} />
          <select className="cnet-select" value={dTU} onChange={e => setDTU(e.target.value)}><option>Celsius</option><option>Fahrenheit</option></select>
        </CNetRow>
        <div className="text-xs text-slate-500 pl-[188px] -mt-1 mb-1">e.g. 75°F for Boston winter, 45°F for Atlanta winter.</div>
        <CNetActions onCalc={calc2} onClear={clear2} testId="btu2" />
      </div>

      {res2 && (
        <CNetResult>
          <div className="text-xl font-semibold text-slate-900" data-testid="btu2-result">Required Heating/Cooling Capacity: {G(res2.btu, 0)} BTU/h</div>
          <div className="text-sm text-slate-700 mt-1">≈ {G(res2.btu / 12000, 2)} tons · {G(res2.btu * 0.29307107, 0)} W</div>
        </CNetResult>
      )}
    </CalcShell>
  );
}
